import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table, Badge, Spinner, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { GameSpaceInfo, GameSession } from '../../types';
import gameService from '../../services/gameService';

interface GameSessionManagementProps {
  gameSpaceId: number;
}

const GameSessionManagement: React.FC<GameSessionManagementProps> = ({ gameSpaceId }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [gameSpace, setGameSpace] = useState<GameSpaceInfo | null>(null);
  const [gameSessions, setGameSessions] = useState<GameSession[]>([]);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadGameSpaceDetails();
    loadGameSessions();
  }, [gameSpaceId]);

  const loadGameSpaceDetails = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await gameService.getGameSpace(gameSpaceId);
      
      if (response.success) {
        setGameSpace(response.data);
      } else {
        setError(response.message || 'Failed to load game space details');
      }
    } catch (err) {
      setError('An unexpected error occurred while loading game space details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadGameSessions = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // We'll need to implement this endpoint in the backend
      const response = await gameService.getGameSessions(gameSpaceId);
      
      if (response.success) {
        setGameSessions(response.data);
      } else {
        setError(response.message || 'Failed to load game sessions');
      }
    } catch (err) {
      setError('An unexpected error occurred while loading game sessions');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSession = () => {
    navigate(`/game/create?gameSpaceId=${gameSpaceId}`);
  };

  const handleViewSession = (sessionId: number) => {
    navigate(`/game/session/${sessionId}`);
  };

  const getStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return <Badge bg="success">Active</Badge>;
      case 'scheduled':
        return <Badge bg="primary">Scheduled</Badge>;
      case 'completed':
        return <Badge bg="secondary">Completed</Badge>;
      case 'cancelled':
        return <Badge bg="danger">Cancelled</Badge>;
      default:
        return <Badge bg="info">{status}</Badge>;
    }
  };

  if (loading && !gameSpace) {
    return (
      <Container className="mt-4 text-center">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      {error && <Alert variant="danger">{error}</Alert>}
      
      {gameSpace && (
        <>
          <Row className="mb-4">
            <Col>
              <h2>{gameSpace.title || gameSpace.name}</h2>
              <p className="text-muted">{gameSpace.description}</p>
              
              <Card className="mb-4">
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <h5>Game Space Details</h5>
                      <Table borderless size="sm">
                        <tbody>
                          <tr>
                            <td><strong>Game Code:</strong></td>
                            <td>{gameSpace.gameCode || 'N/A'}</td>
                          </tr>
                          <tr>
                            <td><strong>Join Code:</strong></td>
                            <td>{gameSpace.joinCode || 'N/A'}</td>
                          </tr>
                          <tr>
                            <td><strong>Visibility:</strong></td>
                            <td>{gameSpace.isPublic ? 'Public' : 'Private'}</td>
                          </tr>
                          <tr>
                            <td><strong>Max Players:</strong></td>
                            <td>{gameSpace.maxPlayers}</td>
                          </tr>
                          <tr>
                            <td><strong>Created:</strong></td>
                            <td>{new Date(gameSpace.createdAt).toLocaleDateString()}</td>
                          </tr>
                        </tbody>
                      </Table>
                    </Col>
                    <Col md={6} className="d-flex flex-column align-items-center justify-content-center">
                      <div className="text-center">
                        <h5>Share Game Space</h5>
                        {gameSpace.joinCode && (
                          <div className="mb-3">
                            <div className="d-flex justify-content-center mb-2">
                              {/* QR Code would go here */}
                              <div className="border p-4 text-center">
                                QR Code
                              </div>
                            </div>
                            <Button variant="outline-primary" size="sm">
                              <i className="fas fa-copy me-1"></i> Copy Join Link
                            </Button>
                          </div>
                        )}
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4>Game Sessions</h4>
                <Button variant="primary" onClick={handleCreateSession}>
                  <i className="fas fa-plus me-1"></i> Create New Session
                </Button>
              </div>

              {gameSessions.length === 0 ? (
                <Alert variant="info">
                  No game sessions found. Create your first game session to get started!
                </Alert>
              ) : (
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Session Name</th>
                      <th>Status</th>
                      <th>Players</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gameSessions.map(session => (
                      <tr key={session.id}>
                        <td>{session.name}</td>
                        <td>{getStatusBadge(session.status)}</td>
                        <td>{session.playerCount} / {session.maxPlayers}</td>
                        <td>{new Date(session.createdAt).toLocaleDateString()}</td>
                        <td>
                          <Button 
                            variant="outline-primary" 
                            size="sm" 
                            onClick={() => handleViewSession(session.id)}
                            className="me-2"
                          >
                            View
                          </Button>
                          {session.status.toLowerCase() === 'active' && (
                            <Button variant="success" size="sm" className="me-2">
                              Join
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

export default GameSessionManagement;
